import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Typography,
  DatePicker,
  Flex,
  Image,
  Dropdown,
  App,
  Row,
  Col,
  Select,
  Radio,
} from "antd";
import DatePickerIcon from "../../assets/icons/datepicker-calendar.svg";
import { ArrowDownOutlined } from "@ant-design/icons";
import PdfIcon from "../../assets/icons/pdf-icon.svg";
import CsvIcon from "../../assets/icons/csv-icon.svg";
import dayjs from "dayjs";
import axios from "../../config/httpConfig";
import CustomButton from "../../components/CustomButton/Button";
import { downloadBlob, getDownloadSuccessMessage } from "../../helpers/helpers";
import "./Reports.scss";

const { Option } = Select;
const { Text } = Typography;

const filtersByTitle = {
  "Transaction Summary": ["timePeriod", "tenure", "txnStatus"],
  "Deal Wise Profit Statement": ["timePeriod", "industryType", "dealId"],
};

const filterLabels = {
  timePeriod: "Time Period",
  industryType: "Industry Type",
  tenure: "Tenure",
  txnStatus: "Status",
  dealId: "Deal Id",
};

const tenureOptions = [
  { label: "Last 12 months transaction details", value: "12m" },
  { label: "Last 6 months transaction details", value: "6m" },
  { label: "Last 3 months transaction details ", value: "3m" },
  { label: "Last One Month", value: "1m" },
  { label: "Last Week", value: "1w" },
  { label: "Current", value: "today" },
];

const ReportsCollapseContent = ({ type, title }) => {
  const { message } = App.useApp();
  const { reportsDropdownData } = useSelector((state) => state.reports);
  const [selectedFilter, setSelectedFilter] = useState("timePeriod");
  const [selectedTenure, setSelectedTenure] = useState("");
  const [selectedValue, setSelectedValue] = useState({
    startDate: null,
    endDate: null,
    industryType: null,
    tenureMin: null,
    tenureMax: null,
    txnStatus: null,
    dealId: null,
  });

  const isDateRangeIncomplete =
    selectedFilter === "timePeriod" &&
    (!selectedValue.startDate || !selectedValue.endDate);

  const handleInputChange = (name, value) => {
    setSelectedValue((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const downloadReport = async (format, type) => {
    try {
      const response = await axios.get("/api/investor/getInvestorReports", {
        params: {
          type,
          format,
          startDate: selectedValue.startDate,
          endDate: selectedValue.endDate,
          tenure: {
            min: selectedValue?.tenureMin,
            max: selectedValue?.tenureMax,
          },
          status: selectedValue?.txnStatus,
          industryType: selectedValue?.industryType,
          dealId: selectedValue?.dealId,
        },
        responseType: "blob",
      });

      const contentType = response.type;

      // If it's not a valid file (e.g. error JSON), parse it and show message
      if (contentType === "application/json") {
        const text = await response.text();
        const result = JSON.parse(text);
        throw new Error(result?.response?.message || "No data found");
      }

      // ✅ Valid blob, proceed with download
      const fileName =
        type === "1" ? "Transaction Summary Report" : "Deal Wise Profit Report";
      const downloadedFileName = `${fileName}.${format === "pdf" ? "pdf" : "xlsx"}`;
      downloadBlob(response, downloadedFileName);
      message.success(getDownloadSuccessMessage(downloadedFileName));
    } catch (error) {
      message.error(
        error.message ||
          "Failed to download the report. Please try again later.",
      );
    }
  };

  const calculateTenureRange = (value) => {
    const end = dayjs();
    let start;

    switch (value) {
      case "12m":
        start = end.subtract(12, "month");
        break;
      case "6m":
        start = end.subtract(6, "month");
        break;
      case "3m":
        start = end.subtract(3, "month");
        break;
      case "1m":
        start = end.subtract(1, "month");
        break;
      case "1w":
        start = end.subtract(7, "day");
        break;
      case "today":
      default:
        start = end;
        break;
    }

    return {
      tenureMin: start.format("YYYY-MM-DD"),
      tenureMax: end.format("YYYY-MM-DD"),
    };
  };

  const disableFutureDates = (current) =>
    current && current > dayjs().endOf("day");

  const renderFilterField = () => {
    switch (selectedFilter) {
      case "timePeriod":
        return (
          <Flex gap={10}>
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="Start Date"
              value={
                selectedValue.startDate ? dayjs(selectedValue.startDate) : null
              }
              suffixIcon={
                <Image src={DatePickerIcon} alt="Calendar" preview={false} />
              }
              onChange={(date) => {
                handleInputChange(
                  "startDate",
                  date ? date.format("YYYY-MM-DD") : null,
                );

                handleInputChange("endDate", null);
              }}
              disabledDate={disableFutureDates}
              style={{ width: "100%" }}
            />
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="End Date"
              value={
                selectedValue.endDate ? dayjs(selectedValue.endDate) : null
              }
              suffixIcon={
                <Image src={DatePickerIcon} alt="Calendar" preview={false} />
              }
              onChange={(date) =>
                handleInputChange(
                  "endDate",
                  date ? date.format("YYYY-MM-DD") : null,
                )
              }
              disabledDate={(current) => {
                if (current && current > dayjs().endOf("day")) {
                  return true;
                }

                if (selectedValue.startDate) {
                  return Boolean(
                    current &&
                      current.isBefore &&
                      current.isBefore(dayjs(selectedValue.startDate), "day"),
                  );
                }

                return false;
              }}
              style={{ width: "100%" }}
            />
          </Flex>
        );
      case "tenure":
        return (
          <Select
            style={{ width: "100%" }}
            placeholder="Select Duration"
            value={selectedTenure || undefined}
            onChange={(value) => {
              setSelectedTenure(value);
              const { tenureMin, tenureMax } = calculateTenureRange(value);
              handleInputChange("tenureMin", tenureMin);
              handleInputChange("tenureMax", tenureMax);
            }}
          >
            {tenureOptions.map(({ label, value }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
        );
      default:
        return (
          <Select
            style={{ width: "100%" }}
            placeholder={`Select ${filterLabels[selectedFilter]}`}
            value={selectedValue[selectedFilter] || undefined}
            onChange={(value) => handleInputChange(selectedFilter, value)}
          >
            {Array.isArray(reportsDropdownData[selectedFilter]) &&
              reportsDropdownData[selectedFilter].map((option, index) => {
                // Handle object type (txnStatus) and string type (industryType, dealId)
                if (typeof option === "object" && option?.statusId) {
                  return (
                    <Option key={option?.statusId} value={option?.statusId}>
                      {option?.statusName}
                    </Option>
                  );
                } else {
                  return (
                    <Option key={index} value={option}>
                      {option}
                    </Option>
                  );
                }
              })}
          </Select>
        );
    }
  };

  return (
    <div className="collapse-item-content-container">
      <Row gutter={[16, 16]} align="middle" wrap>
        <Col span={24}>
          <Radio.Group
            value={selectedFilter}
            onChange={(e) => {
              setSelectedFilter(e.target.value);
              setSelectedValue({});
              setSelectedTenure("");
            }}
            options={filtersByTitle[title]?.map((filter) => ({
              label: filterLabels[filter],
              value: filter,
            }))}
          />
        </Col>
        <Col xs={24} md={10} lg={4}>
          <Text style={{ color: "#676767", fontSize: 14 }}>
            {filterLabels[selectedFilter]}
          </Text>
        </Col>
        <Col xs={24} md={24} lg={14}>
          {renderFilterField()}
        </Col>
        <Col xs={24} md={24} lg={6} style={{ textAlign: "right" }}>
          <Dropdown
            menu={{
              items: [
                {
                  key: "1",
                  icon: (
                    <Image
                      width={20}
                      src={PdfIcon}
                      alt="PDF-Icon"
                      preview={false}
                    />
                  ),
                  label: (
                    <span
                      onClick={() => downloadReport("pdf", type)}
                      style={{ margin: 10 }}
                    >
                      Download as PDF
                    </span>
                  ),
                },
                { type: "divider" },
                {
                  key: "2",
                  icon: (
                    <Image
                      width={20}
                      src={CsvIcon}
                      alt="CSV-Icon"
                      preview={false}
                    />
                  ),
                  label: (
                    <span
                      onClick={() => downloadReport("xlsx", type)}
                      style={{ margin: 10 }}
                    >
                      Download as CSV
                    </span>
                  ),
                },
              ],
            }}
            trigger={["click"]}
            arrow={{ pointAtCenter: true }}
            placement="bottomCenter"
            overlayStyle={{
              border: "1px solid #d9d9d9",
              borderRadius: "8px",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CustomButton
              variant="solid"
              color="primary"
              icon={<ArrowDownOutlined />}
              iconPosition="end"
              disabled={isDateRangeIncomplete}
            >
              Download
            </CustomButton>
          </Dropdown>
        </Col>
      </Row>
    </div>
  );
};

export default ReportsCollapseContent;
